import Badge from '@/common/components/Badge'
import Box from '@/common/components/Box'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import { UnitTextField } from '@/common/components/UnitTextField'
import { unities, unitiesDecimals } from '@/common/constants/unities.constant'
import { formatCurrency, formatDecimalUnit } from '@/common/utils/formatters'
import {
  CompanyGovernmentAllocationProductDistribution,
  ControlledByType,
  GovernmentAllocationAllowedCategoryAndProductData,
} from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { FileBoxIcon } from 'lucide-react'

interface ProductDistributionItemProps {
  productDistribution: CompanyGovernmentAllocationProductDistribution
  allowedProduct: GovernmentAllocationAllowedCategoryAndProductData
  controlledBy?: ControlledByType
}

export const ProductDistributionItem = ({
  productDistribution,
  allowedProduct,
  controlledBy = 'currency',
}: ProductDistributionItemProps) => {
  const isQuantity = controlledBy === 'quantity'

  const currentValue = isQuantity ? productDistribution?.total_quantity : productDistribution?.total_value

  const allocated = Number(isQuantity ? allowedProduct?.allocated_quantity : allowedProduct?.allocated_total) || 0
  const available = Number(isQuantity ? allowedProduct?.available_quantity : allowedProduct?.available_total) || 0
  const distributed = Number(isQuantity ? allowedProduct?.distributed_quantity : allowedProduct?.distributed_total) || 0

  const availableForAllocation =
    Math.min(
      Number(currentValue) || 0,
      Number(isQuantity ? productDistribution?.total_quantity : productDistribution?.total_value) || 0,
    ) + available

  return (
    <Box className="flex flex-col gap-4">
      <Badge
        Icon={FileBoxIcon}
        iconProps={{ className: 'size-6', strokeWidth: 1 }}
        text={productDistribution?.product?.display_name}
        className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 pe-2 gap-0"
        overwriteClassName
        contentClassName="px-3 py-2"
      />

      <div className="flex flex-col gap-1">
        {isQuantity ? (
          <UnitTextField
            label="Quantidade limite"
            unit={
              productDistribution.product.unit_for_quantity
                ? unities[productDistribution.product.unit_for_quantity]
                : undefined
            }
            decimals={
              productDistribution.product.unit_for_quantity
                ? unitiesDecimals[productDistribution.product.unit_for_quantity]
                : 2
            }
            value={formatDecimalUnit(
              Number(productDistribution.total_quantity),
              productDistribution.product.unit_for_quantity,
            )}
            disabled
          />
        ) : (
          <CurrencyTextField
            label="Valor limite"
            value={formatCurrency(Number(productDistribution.total_value) || 0)}
            disabled
          />
        )}

        <div className="flex gap-3">
          <p className="italic text-xs text-white-400">
            {isQuantity ? 'Quantidade maxima: ' : 'Total máximo: '}
            <span className="whitespace-nowrap">
              {isQuantity
                ? formatDecimalUnit(availableForAllocation, productDistribution.product.unit_for_quantity)
                : formatCurrency(availableForAllocation)}
            </span>
          </p>

          <p className="italic text-xs text-white-400">
            {isQuantity ? 'Quantidade disponível: ' : 'Total disponível: '}
            <span className="whitespace-nowrap">
              {isQuantity
                ? formatDecimalUnit(available, productDistribution.product.unit_for_quantity)
                : formatCurrency(available)}
            </span>
          </p>

          <p className="italic text-xs text-white-400">
            {isQuantity ? 'Quantidade empenhada: ' : 'Total empenhado: '}
            <span className="whitespace-nowrap">
              {isQuantity
                ? allocated
                  ? formatDecimalUnit(allocated, productDistribution.product.unit_for_quantity)
                  : '--'
                : allocated
                  ? formatCurrency(allocated)
                  : '--'}
            </span>
          </p>

          <p className="italic text-xs text-white-400">
            {isQuantity ? 'Quantidade distribuída em todas filiais: ' : 'Total distribuído em todas filiais: '}
            <span className="whitespace-nowrap">
              {isQuantity
                ? distributed
                  ? formatDecimalUnit(distributed, productDistribution.product.unit_for_quantity)
                  : '--'
                : distributed
                  ? formatCurrency(distributed)
                  : '--'}
            </span>
          </p>
        </div>
      </div>
    </Box>
  )
}
